import { type ReactElement } from 'react'
import { FormattedMessage } from 'react-intl'

import {
  type PimProductV11,
  PimProductV1StockStatusEnum,
} from '@redteclab/api/clients/bully'

import { useGetExperiment } from '../../../../experiments/hooks'
import { EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS } from '../../../../experiments/model/EXPERIMENT_NAME'
import { EXPERIMENT_VARIATION } from '../../../../experiments/model/EXPERIMENT_VARIATION'
import { productAccessorGetOutOfStockMessageDays } from '../../../model/productAccessor/productAccessorGetOutOfStockMessageDays'
import { productAccessorIsSaeOffer } from '../../../model/productAccessor/productAccessorIsSaeOffer'
import { productAccessorStockIsAvailableButHasNotOnStockMessage } from '../../../model/productAccessor/productAccessorStockIsAvailableButHasNotOnStockMessage'
import { useProductPageContext } from '../../productPageContext'

export type ProductStockStatus = {
  bgColor: string
  color: string
  dotColor: string
  message: ReactElement | null
}

export const PRODUCT_STOCK_STATUS_DEFAULT = {
  bgColor: '',
  color: '',
  dotColor: '',
  message: null,
}

export const getWillBeDeliveredLater = (
  product: PimProductV11,
): ProductStockStatus => {
  // e.g. 5-7
  const daysTillBeDelivered = productAccessorGetOutOfStockMessageDays(product)

  // take first from e.g. 5-7 => 5, 10-20 => 10
  const [firstDay] = daysTillBeDelivered.split('-')

  if (!firstDay) {
    return {
      bgColor: 'bg-dark-now',
      color: 'text-dark-brand-medium',
      dotColor: 'bg-dark-hover-success',
      message: <FormattedMessage id="general.label.product.available" />,
    }
  }

  // less than 7 days - green otherwise - yellow
  const color =
    firstDay < '7' ? 'text-dark-brand-medium' : 'text-dark-note-medium'
  const bgColor = firstDay < '7' ? 'bg-dark-now' : 'bg-dark-note-medium'

  return {
    bgColor,
    color,
    dotColor: 'bg-[theme(textColor[dark-note-medium])]',
    message: (
      <FormattedMessage
        id="general.label.shippingPeriod"
        values={{ deliveryTime: daysTillBeDelivered }}
      />
    ),
  }
}

export const stockStatusMap: Partial<
  Record<PimProductV1StockStatusEnum, ProductStockStatus>
> = {
  [PimProductV1StockStatusEnum.Available]: {
    bgColor: 'bg-dark-hover-success',
    color: 'text-dark-success-medium',
    dotColor: 'bg-dark-hover-success',
    message: <FormattedMessage id="general.label.product.available" />,
  },
  [PimProductV1StockStatusEnum.Free]: {
    bgColor: 'bg-dark-hover-success',
    color: 'text-dark-success-medium',
    dotColor: 'bg-dark-hover-success',
    message: <FormattedMessage id="general.label.product.gratis" />,
  },
  [PimProductV1StockStatusEnum.NotInCatalog]: {
    bgColor: 'bg-dark-accent',
    color: 'text-dark-brand-medium',
    dotColor: 'bg-[theme(textColor[dark-error-strong])]',
    message: <FormattedMessage id="general.label.product.notInAssortment" />,
  },
  [PimProductV1StockStatusEnum.OutOfSale]: {
    bgColor: 'bg-dark-accent',
    color: 'text-dark-brand-medium',
    dotColor: 'bg-[theme(textColor[dark-error-strong])]',
    message: <FormattedMessage id="general.label.product.outOfTrade" />,
  },
  [PimProductV1StockStatusEnum.OutOfStock]: {
    bgColor: 'bg-[theme(textColor[dark-note-strong])]',
    color: 'text-dark-note-strong',
    dotColor: 'bg-[theme(textColor[dark-error-strong])]',
    message: (
      <FormattedMessage id="general.label.product.currentlyNotAvailable" />
    ),
  },
}

/**
 * Checks the stock status of the current variant, figures out the color and message to display and returns it
 */
export const useProductStockStatus = (): ProductStockStatus => {
  const { currentOffer, currentVariant } = useProductPageContext()
  const pdpDeliveryOptions = useGetExperiment(
    EXPERIMENT_NAME_PDP_DELIVERY_OPTIONS,
  )

  const isMarketplace = currentOffer
    ? !productAccessorIsSaeOffer(currentOffer)
    : false

  if (!currentVariant.stock_status) {
    return PRODUCT_STOCK_STATUS_DEFAULT
  }

  // aka will be delivered in 3-5 days
  if (productAccessorStockIsAvailableButHasNotOnStockMessage(currentVariant)) {
    return getWillBeDeliveredLater(currentVariant)
  }

  if (
    currentVariant.stock_status === PimProductV1StockStatusEnum.Available &&
    pdpDeliveryOptions.isEnabled &&
    !isMarketplace &&
    pdpDeliveryOptions.variant !== EXPERIMENT_VARIATION.DEFAULT
  ) {
    return {
      bgColor: 'bg-dark-hover-success',
      color: 'text-dark-success-medium',
      dotColor: 'bg-dark-hover-success',
      message: <FormattedMessage id="general.label.product.availableDays" />,
    }
  }

  return (
    stockStatusMap[currentVariant.stock_status] ?? PRODUCT_STOCK_STATUS_DEFAULT
  )
}
